// navigation-visual-link-selection — resolve a SAVED cross-document link back to
// its authorised inventory target, so a reopened remote link can show where it
// points and preview the EXACT target bytes without ever fetching a raw href.
//
// A saved GoToR link only carries a source-relative path string (e.g.
// `../2-5/clinical-overview.pdf`). The authoritative identity (opaque target id,
// context version, target hash) lives in the current remote-target inventory. We
// recover it by matching that path against each inventory target's SERVER-SUPPLIED
// `href` — the one canonical `hrefToTarget`/`computeRelativeHref` value, never a
// re-implemented path algorithm on the client (one evaluator per rule). The
// matched target's id + hash then flow through the existing preview / resolve
// APIs, which re-assert the context version and byte hash.
//
// SEMANTICS (current-resolution, deliberate): this resolves a saved link to the
// target that lives at its path NOW. That is exactly what the GoToR link does at
// runtime — it jumps to its coordinate in whatever file currently sits at that
// relative path — and it is the only honest option for links imported WITHOUT a
// frozen dependency. It never invents a frozen identity: the preview verifies the
// CURRENT bytes against the CURRENT hash, and any retarget re-verifies on save via
// `resolveRemoteTarget`. Frozen-dependency currency (did the delivered target
// change since save) is enforced separately at save/compile, unchanged here.

import type { NavigationTarget, TargetInventory } from '@/api/navigationTargets';

export type SavedRemoteMatch =
  | { readonly kind: 'ok'; readonly target: NavigationTarget }
  | { readonly kind: 'not-found' }
  | { readonly kind: 'ambiguous' }
  | { readonly kind: 'unavailable'; readonly reason: string };

/**
 * Resolve a saved remote link's relative `href` to its unique authorised target
 * in the current inventory, by exact match against the server-supplied
 * `target.href`, to recover the id/hash/context needed to preview or re-select it.
 * Zero matches ⇒ not-found; more than one ⇒ ambiguous (refuse rather than guess);
 * a single match that is not addressable ⇒ unavailable with the server's reason.
 *
 * This is RESOLUTION only — it does NOT judge frozen identity. The canonical
 * saved-target CURRENCY (did an authored link's target change since save) is the
 * server's per-link `InventoryLink.savedTarget` verdict (one rule,
 * `matchDependencyInInventory`); callers gate on that BEFORE resolving here.
 */
export function matchSavedRemoteTarget(inventory: TargetInventory, href: string): SavedRemoteMatch {
  const matches = inventory.targets.filter((t) => t.href === href);
  if (matches.length === 0) return { kind: 'not-found' };
  if (matches.length > 1) return { kind: 'ambiguous' };
  const target = matches[0]!;
  if (!target.available) {
    return { kind: 'unavailable', reason: target.unavailableReason ?? 'the saved target cannot be addressed as a page/named destination' };
  }
  return { kind: 'ok', target };
}

/** A reviewer-facing message for a non-ok match (honest refusal, never a guess). */
export function describeSavedRemoteMiss(match: Exclude<SavedRemoteMatch, { kind: 'ok' }>): string {
  switch (match.kind) {
    case 'not-found':
      return 'the saved target is no longer in this submission’s link inventory — reselect the destination';
    case 'ambiguous':
      return 'the saved target now matches more than one document — reselect the destination to disambiguate';
    case 'unavailable':
      return `the saved target is not addressable: ${match.reason}`;
    default: {
      const _never: never = match;
      return String(_never);
    }
  }
}
